import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../../hooks/useTranslation';

/**
 * One participant's pain point inside a cluster or the unclustered list:
 * the sentence as written, who wrote it, and what the interview and the
 * mirror produced. Never editable here; the participant's words stay theirs.
 */

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px 12px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 13px;
`;

const Text = styled.div`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
`;

const Meta = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  b { color: rgba(255, 255, 255, 0.75); font-weight: 600; }
  .not_here { color: #ff8a85; }
  .holds { color: #6fd3c8; }
  .unsure { color: #f2c14e; }
`;

const Select = styled.select`
  background: #0f131b;
  color: #e8ecf2;
  border: 1px solid #2c3444;
  border-radius: 6px;
  padding: 4px 6px;
  font: inherit;
  font-size: 12px;
  max-width: 220px;
`;

const STANCES = ['holds', 'not_here', 'unsure'];

/**
 * @param {object} props
 * @param {object} props.painPoint
 * @param {Array<{id:string,title:string}>} props.targets other clusters this can move to
 * @param {(target: string) => void} [props.onMove] '' = new cluster of its own
 * @param {boolean} [props.locked]
 */
export default function PainPointRow({ painPoint: p, targets = [], onMove = null, locked = false }) {
  const { t } = useTranslation();
  const stances = p.stances.reduce((acc, s) => ({ ...acc, [s]: (acc[s] || 0) + 1 }), {});
  return (
    <Row>
      <Text>{p.text}</Text>
      {onMove && !locked ? (
        <Select value="" onChange={(e) => { if (e.target.value !== '') onMove(e.target.value === 'new' ? '' : e.target.value); }}>
          <option value="">{t('preworkCuration.painPoint.move')}</option>
          <option value="new">{t('preworkCuration.painPoint.ownGroup')}</option>
          {targets.map((target) => <option key={target.id} value={target.id}>{target.title || t('preworkCuration.painPoint.untitled')}</option>)}
        </Select>
      ) : <span />}
      <Meta>
        <span><b>{p.participantName || p.email}</b></span>
        <span>{p.status === 'done' ? t('preworkCuration.painPoint.done') : t('preworkCuration.painPoint.inProgress')}</span>
        {p.causes.length > 0 && <span>{t('preworkCuration.painPoint.causes', { count: p.causes.length })}</span>}
        {p.blockers.length > 0 && <span>{t('preworkCuration.painPoint.blockers', { count: p.blockers.length })}</span>}
        {Object.entries(stances).map(([s, n]) => <span key={s} className={s}>{n} × {STANCES.includes(s) ? t(`preworkCuration.painPoint.stance.${s}`) : s}</span>)}
        {!p.embedded && <span>{t('preworkCuration.painPoint.noVector')}</span>}
      </Meta>
    </Row>
  );
}
